import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { isEmpty, isNumber } from '@helpers/arrays';
import { EmpyreanApplicationName, getUserLevelForApplication, IUserLevel } from '@interfaces/user-level';
import { PermissionsService } from '@services/auth/permissions.service';
import { GlobalParamsService } from '@services/global-params/global-params.service';
import { User, UserManager } from 'oidc-client';
import { ReplaySubject } from 'rxjs';
import { AppConfigProvider } from './../../app.config.provider';
import { TimeoutService } from './timeout.service';

const ENABLE_DEBUGGING = false;

@Injectable({ providedIn: 'root' })
export class OpenIdConnectService {
  private userManager: UserManager;
  private currentUser: User;

  userLoaded$ = new ReplaySubject<boolean>(1);

  get userAvailable(): boolean {
    return Boolean(this.currentUser);
  }

  get user(): User {
    return this.currentUser;
  }

  get userId(): number {
    if (this.userAvailable) {
      return Number(this.currentUser.profile.sub);
    } else {
      this.triggerSignIn();
    }
  }

  get customerId(): number {
    const customer_id = Number(this.currentUser.profile.Custaim);
    if (!isNaN(customer_id) && typeof customer_id === 'number') {
      return customer_id as number;
    } else {
      console.warn('customer_id not found for current user');
      return null;
    }
  }

  private getLiquidityUserLevelFromProfile(profile) {
    return getUserLevelForApplication(EmpyreanApplicationName.Liquidity, JSON.parse(profile.userlevels));
  }

  get userLevelList(): IUserLevel[] {
    if (this.userAvailable && this.currentUser.profile.userlevels) {
      return JSON.parse(this.currentUser.profile.userlevels);
    } else {
      return [{ level_id: -1, level_text: 'User level not found' }];
    }
  }

  get userLevel(): IUserLevel {
    if (this.userAvailable && this.currentUser.profile.userlevels) {
      return this.getLiquidityUserLevelFromProfile(this.currentUser.profile);
    } else {
      return { level_id: -1, level_text: 'User level not found' };
    }
  }

  constructor(
    private readonly router: Router,
    private readonly timeoutService: TimeoutService,
    private readonly permissionService: PermissionsService,
    private readonly appConfig: AppConfigProvider,
    private readonly globalParamsService: GlobalParamsService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.appConfig.environment.subscribe(env => {
      let oidcSettings = env.openIdConnectSettings;

      let { host } = window.location;
      const allowedHost = env.ALLOWED_URLS.find((url: string) => host.includes(url));

      const fragments = host.split(`${allowedHost}`);

      if (allowedHost && fragments && fragments.length > 0 && !isEmpty(fragments[0])) {
        //
        let idpFragment = fragments[0];
        if (idpFragment.indexOf('.') === idpFragment.length - 1) {
          idpFragment = idpFragment.substring(0, idpFragment.indexOf('.'));
        }
        host = host.replace(/\/\s*$/, ''); // removes backlash at the end, if exists
        oidcSettings = {
          ...env.openIdConnectSettings,
          acr_values: `idp:${idpFragment}`,
          silent_redirect_uri: `https://${host}/redirect-silentrenew`,
          redirect_uri: `https://${host}/signin-oidc`,
          post_logout_redirect_uri: `https://${host}`,
        };
      }

      this.userManager = new UserManager(oidcSettings);

      this.userManager.clearStaleState();
      this.userManager.events.addUserLoaded(user => {
        if (ENABLE_DEBUGGING) {
          console.log(`User loaded ${new Date()}`, this.currentUser, { user });
        }

        const incomingUserLevel = this.getLiquidityUserLevelFromProfile(user.profile);
        try {
          if (this.userLevel && incomingUserLevel && this.userLevel.level_id !== incomingUserLevel.level_id) {
            this.currentUser = user;
            this.permissionService
              .loadPermissionsIntoApp(this.userLevel.level_id)
              .then(_ => {
                this.timeoutService.startTracking();
                this.userManager.startSilentRenew();
                this.userLoaded$.next(true);
              })
              .catch(err => {
                console.error(err);
                // this.document.location.href = '/401';
              });

            this.globalParamsService.getParams().subscribe(settings => {
              const maxIdleTime = settings && settings.find(setting => setting.ParamName === 'MAX_IDLE_TIME');
              if (maxIdleTime && isNumber(maxIdleTime.ParamValue)) {
                this.timeoutService.setMaxIdleTime(+maxIdleTime.ParamValue);
              }
            });
          } else {
            if (this.getLiquidityUserLevelFromProfile(user.profile)) {
              this.currentUser = user;
              this.timeoutService.startTracking();
              this.userManager.startSilentRenew();
            } else {
              if (ENABLE_DEBUGGING) console.error('OpenIdConnectService -> No liquidity user found in token');
              this.document.location.href = '/401';
            }
          }
        } catch (err) {
          if (ENABLE_DEBUGGING) console.error('OpenIdConnectService -> Unable to load token', err);
          this.document.location.href = '/401';
        }
      });

      this.userManager.events.addUserUnloaded(() => {
        if (ENABLE_DEBUGGING) {
          console.log('User unloaded');
        }
        this.currentUser = null;
        this.permissionService.clearTabsFromSessionStorage();
        this.userLoaded$.next(false);
        this.timeoutService.stopTracking();
      });

      this.timeoutService.userTimedOut$.subscribe(res => {
        if (res === true) {
          this.onUserTimeout();
        }
      });
    });
  }

  /**
   * Triggers the OIDC routine to send an user to the OIDC endpoint to retrieve the token
   */
  triggerSignIn() {
    this.userManager
      .signinRedirect()
      .then(() => {
        if (ENABLE_DEBUGGING) {
          console.log('Redirection to signin triggered.');
        }
      })
      .catch(err => {
        if (ENABLE_DEBUGGING) console.error('Unhandled error when triggering login in', err);
        this.router.navigate(['/error']);
      });
  }

  /**
   * Triggers the OIDC routine to store the token in the session store and in memory
   */
  handleCallBack() {
    this.userManager
      .signinRedirectCallback()
      .then(user => {
        this.currentUser = user;
        if (ENABLE_DEBUGGING) {
          console.log('Callback after signin triggered.', user);
        }
      })
      .catch(err => {
        if (ENABLE_DEBUGGING) console.error('OpenIdConnectService -> handleCallBack ->', err);
      });
  }

  handleSilentCallback() {
    this.userManager
      .signinSilentCallback()
      .then(user => {
        // this.currentUser = user;
        // this.permissionService.loadPermissionsIntoApp(this.userId);
        if (ENABLE_DEBUGGING) {
          console.log('Callback after silent signin handled.', user);
          // We may need this
          // https://stackoverflow.com/questions/50416649/no-user-in-signinsilentcallback-using-identityserver-and-oidc-client-of-javascri
        }
      })
      .catch(err => {
        if (ENABLE_DEBUGGING) console.error('Unhandled error when silently triggering login in', err);
        this.router.navigate(['/error']);
      });
  }

  /**
   * Triggers the SingOut process for the OIDC library
   */
  triggerSignOut() {
    this.userManager
      .signoutRedirect()
      .then(res => {
        this.permissionService.clearTabsFromSessionStorage();
        if (ENABLE_DEBUGGING) {
          console.log('Redirection to signout triggered.', res);
        }
      })
      .catch(err => {
        if (ENABLE_DEBUGGING) console.error('Unhandled error when signing out', err);
      })
      .finally(() => this.removeUserFromStorage());
  }

  removeUserFromStorage() {
    this.userManager.removeUser();
    this.userManager.revokeAccessToken();
  }

  /**
   * Routine that will be executed after Idle times the user out
   * It removes the popup if still active, then creates a call to the OIDC endpoint to remove the user
   * from the Identity Server and the frontend.
   * Then, it will stop all tracking services and navigate the user to the Timeout page
   */
  private onUserTimeout() {
    try {
      this.permissionService.clearTabsFromSessionStorage();
      this.userManager
        .createSignoutRequest({ id_token_hint: this.user.id_token, extraQueryParams: { reason: 'timeout' } })
        .then(signout_request => {
          this.document.location.href = signout_request.url;
        });
    } catch (err) {
      this.triggerSignOut();
    } finally {
      // This code should never execute. The try/catch should redirect to identity server in any case
      this.document.location.href = '/timeout'; // Bypasses angular router
      this.timeoutService.stopTracking();
      this.userManager.removeUser();
    }
  }
}
